/* ==========================================================================
   API Types
   TypeScript interfaces matching backend Pydantic schemas
   ========================================================================== */

/* --------------------------------------------------------------------------
   Enums (matching backend/app/models/enums.py)
   Using const objects for TypeScript erasableSyntaxOnly compatibility
   -------------------------------------------------------------------------- */

export const ProviderStatus = {
  INVITED: 'invited',
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const

export type ProviderStatus =
  (typeof ProviderStatus)[keyof typeof ProviderStatus]

export const PrescriptionStatus = {
  SUBMITTED: 'submitted',
  FAX_SENT: 'fax_sent',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  // DELIVERED means the *fax* reached the pharmacy. SHIPPED means the package was
  // dispatched and a tracking number is on record — only reachable for pharmacies
  // with shipment tracking enabled (PORTAL-146), so it is deliberately absent from
  // the status filter dropdowns every other pharmacy sees.
  SHIPPED: 'shipped',
} as const

export type PrescriptionStatus =
  (typeof PrescriptionStatus)[keyof typeof PrescriptionStatus]

export const FaxDeliveryStatus = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
} as const

export type FaxDeliveryStatus =
  (typeof FaxDeliveryStatus)[keyof typeof FaxDeliveryStatus]

/* --------------------------------------------------------------------------
   Auth Types (matching backend/app/schemas/auth.py)
   -------------------------------------------------------------------------- */

export interface SetPasswordRequest {
  token: string
  password: string
}

export interface LoginRequest {
  email: string
  password: string
  remember_me?: boolean
  /** Portal slug being logged into; scopes the lookup so emails that exist in
   *  multiple pharmacies resolve to the right account. */
  portal_slug?: string
  /** "Remember this device" token from an earlier 2FA login (PORTAL-187). */
  two_factor_device_token?: string
}

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_at: string // ISO datetime string
  /** Set only by the 2FA code step when the device should be remembered. */
  two_factor_device_token?: string | null
}

export interface LoginResponse extends TokenResponse {
  portal_slug: string
}

/* Pharmacy selection (PORTAL-199) */

/** A pharmacy portal the provider can sign in to. */
export interface PortalOption {
  portal_slug: string
  pharmacy_name: string
}

/** Returned by a provider login without a portal slug when the credentials are
 *  valid at more than one pharmacy. Log in again with the chosen portal_slug. */
export interface PortalSelectionResponse {
  portal_selection_required: true
  portals: PortalOption[]
}

export function isPortalSelection(
  response: unknown
): response is PortalSelectionResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'portal_selection_required' in response &&
    response.portal_selection_required === true
  )
}

/* Two-factor authentication (PORTAL-187) */

/** Returned by login instead of a token when the account has 2FA on. */
export interface TwoFactorChallengeResponse {
  two_factor_required: true
  /** 5-minute pass for the code step. Not an access token. */
  two_factor_token: string
  expires_at: string
}

export function isTwoFactorChallenge(
  response: unknown
): response is TwoFactorChallengeResponse {
  // Guard first: a proxy error page can arrive as a string body, and `in` throws
  // on non-objects.
  return (
    typeof response === 'object' &&
    response !== null &&
    'two_factor_required' in response &&
    response.two_factor_required === true
  )
}

export interface TwoFactorLoginRequest {
  two_factor_token: string
  code: string
  remember_device?: boolean
}

export interface TwoFactorStatusResponse {
  enabled: boolean
}

export interface TwoFactorSetupResponse {
  secret: string
  /** otpauth:// link rendered as a QR code. */
  otpauth_uri: string
}

/** A password login that is waiting for its authenticator code. */
export interface PendingTwoFactorLogin {
  /** The challenge pass from the password step. */
  token: string
  email: string
  rememberMe: boolean
}

/** The calls a 2FA settings card needs; each portal passes its own. */
export interface TwoFactorSettingsApi {
  getStatus: () => Promise<TwoFactorStatusResponse>
  startSetup: () => Promise<TwoFactorSetupResponse>
  enable: (code: string) => Promise<TwoFactorStatusResponse>
  disable: (code: string) => Promise<TwoFactorStatusResponse>
}

export interface ForgotPasswordRequest {
  email: string
  /** Portal slug to scope the reset to the right account when an email spans
   *  multiple pharmacies. */
  portal_slug?: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface MessageResponse {
  message: string
}

export const PatientType = {
  HUMAN: 'human',
  ANIMAL: 'animal',
} as const

export type PatientType = (typeof PatientType)[keyof typeof PatientType]

export const PortalType = {
  HUMAN: 'human',
  VETERINARY: 'veterinary',
  BOTH: 'both',
} as const

export type PortalType = (typeof PortalType)[keyof typeof PortalType]

export const CredentialType = {
  MD: 'md',
  DO: 'do',
  NP: 'np',
  PA: 'pa',
  DDS: 'dds',
  DMD: 'dmd',
  DPM: 'dpm',
  OD: 'od',
  DVM: 'dvm',
  ND: 'nd',
  NMD: 'nmd',
  MB: 'mb',
  OTHER: 'other',
} as const

export type CredentialType =
  (typeof CredentialType)[keyof typeof CredentialType]

export const PaymentType = {
  PATIENT_PAY: 'patient_pay',
  PROVIDER_PAY: 'provider_pay',
} as const

export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType]

export const DeliveryMethod = {
  PATIENT_PICKUP: 'patient_pickup',
  PATIENT_SHIP: 'patient_ship',
  PROVIDER_PICKUP: 'provider_pickup',
  PROVIDER_SHIP: 'provider_ship',
} as const

export type DeliveryMethod =
  (typeof DeliveryMethod)[keyof typeof DeliveryMethod]

/* --------------------------------------------------------------------------
   Product Note Types
   -------------------------------------------------------------------------- */

export interface ProductNoteDefinition {
  label: string
  value: string
}

/* --------------------------------------------------------------------------
   Custom Field Types
   -------------------------------------------------------------------------- */

export interface CustomFieldDefinition {
  key: string
  label: string
  type: 'text' | 'number' | 'date' | 'dropdown' | 'checkbox'
  required: boolean
  options: string[] | null
  placeholder: string | null
  helper_text: string | null
  // PORTAL-181: general (pharmacy-level) fields only. null/absent = asked of
  // every provider; non-empty list = those providers; [] = no one (parked, or
  // its last assignee was deleted). Ignored by the backend on product fields.
  provider_ids?: string[] | null
}

/* --------------------------------------------------------------------------
   Portal Types (matching backend/app/schemas/portal.py)
   -------------------------------------------------------------------------- */

export interface PortalConfigResponse {
  id: string // UUID
  pharmacy_name: string
  portal_slug: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  landing_page_enabled: boolean
  show_catalog_on_landing_page: boolean
  show_invite_form_on_landing_page: boolean
  portal_type: PortalType
  /** ISO-3166 alpha-2 (PORTAL-161). 'CA' localizes labels and hides NPI/DEA. */
  country: string
  signature_required: boolean
  /** Derived: always false for Canadian pharmacies. */
  npi_required: boolean
  dea_required: boolean
  patient_address_required: boolean
  provider_address_lock: boolean
  hide_payment_delivery_section: boolean
  hide_diagnosis_code: boolean
  hide_payment_type: boolean
  hide_delivery_method: boolean
  diagnosis_code_required: boolean
  payment_type_required: boolean
  delivery_method_required: boolean
  hide_category_browse: boolean
  directions_dropdown_enabled: boolean
  use_clinic_pay_label: boolean
  /** PORTAL-192: show manually entered tracking numbers in History. */
  manual_tracking_enabled: boolean
}

export interface CatalogProductForPortal {
  id: string // UUID
  display_name: string
  product_code: string
  ndc: string | null
  ingredients: string | null
  strength: string | null
  dose_specific_directions: string | null
  default_additional_directions: string | null
  quantity: string | null // Decimal as string
  unit_amount: string
  days_supply: number | null
  refills: number | null
  medical_necessity_statement: string | null
  categories: string[]
  custom_fields: CustomFieldDefinition[] | null
  product_notes: ProductNoteDefinition[] | null
}

export interface PortalCatalogResponse {
  products: CatalogProductForPortal[]
  total: number
}

export interface PatientAddress {
  street: string
  city: string
  state: string // US state or CA province code
  zip: string // ZIP or postal code
  country?: string // ISO-3166 alpha-2; defaults to 'US' server-side (PORTAL-161)
}

export type PatientGender = 'male' | 'female' | 'nonbinary' | 'unknown'

export interface PrescriptionSubmit {
  // Patient type toggle
  patient_type?: PatientType

  // Patient information (required for human, optional at interface level)
  patient_first_name?: string | null
  patient_last_name?: string | null
  patient_dob?: string | null // ISO date string (YYYY-MM-DD)
  patient_gender?: PatientGender | null
  patient_phone?: string | null

  // Patient information (optional)
  patient_middle_name?: string | null
  patient_address?: PatientAddress | null
  patient_allergies?: string | null
  no_known_allergies?: boolean
  current_medications?: string | null
  no_current_medications?: boolean

  // Animal patient information (required for animal, optional at interface level)
  animal_name?: string | null
  animal_species?: string | null
  animal_breed?: string | null
  owner_name?: string | null
  owner_phone?: string | null
  patient_weight?: string | null
  patient_weight_unit?: string | null
  animal_dob_unknown?: boolean

  // Product selection (required)
  product_id: string // UUID

  // Provider practice address / office this prescription is coming from
  // (issues #140/#180). Optional — if omitted, backend falls back to the
  // provider's primary address.
  provider_address_id?: string | null // UUID

  // Optional modifications
  dose_specific_directions?: string | null
  quantity?: string | null // Decimal as string
  unit_amount?: string | null
  days_supply?: number | null
  refills?: number | null

  // Custom field values
  custom_field_values?: Record<string, string | number | boolean> | null

  // Diagnosis code (ICD-10)
  diagnosis_code?: string | null

  // Payment and delivery
  payment_type?: PaymentType | null
  delivery_method?: DeliveryMethod | null

  // Provider electronic signature (base64-encoded PNG data URL)
  provider_signature?: string | null
}

export interface PrescriptionResponse {
  id: string // UUID
  reference_id: string
  pharmacy_id: string // UUID
  provider_id: string // UUID
  catalog_product_id: string | null // UUID

  // Patient type
  patient_type: string

  // Patient info
  patient_first_name: string | null
  patient_last_name: string | null
  patient_dob: string | null
  patient_gender: PatientGender | null

  // Animal patient info
  animal_name: string | null
  animal_species: string | null
  animal_breed: string | null
  owner_name: string | null
  owner_phone: string | null
  patient_weight: string | null
  patient_weight_unit: string | null
  animal_dob_unknown: boolean

  // Drug information
  drug_name: string
  product_code: string
  drug_ndc: string | null
  ingredients: string | null

  // Prescription details
  strength: string | null
  concentration: string | null // historical
  dose_amount: string | null // historical
  dose_specific_directions: string
  default_additional_directions: string | null
  quantity_per_product: string | null // historical
  quantity: string // Decimal as string
  unit_amount: string | null
  quantity_unit: string // historical
  days_supply: number
  refills: number

  // Modification flags
  dose_specific_directions_modified: boolean
  quantity_modified: boolean
  days_supply_modified: boolean
  refills_modified: boolean

  // Custom field values
  custom_field_values: Record<string, string | number | boolean> | null

  // Patient additional fields
  no_known_allergies: boolean
  current_medications: string | null
  no_current_medications: boolean

  // Diagnosis, payment, delivery
  diagnosis_code: string | null
  payment_type: PaymentType | null
  delivery_method: DeliveryMethod | null

  // Status
  status: PrescriptionStatus
  submitted_at: string // ISO datetime string
}

export interface PrescriptionSubmitResponse {
  reference_id: string
  prescription_id: string // UUID
  status: PrescriptionStatus
  message: string
}

/**
 * Shared patient + diagnosis/payment/delivery fields for a bulk submission.
 * Mirrors backend `PrescriptionPatientPayload`.
 */
export interface PrescriptionPatientPayload {
  patient_type?: PatientType

  // Human
  patient_first_name?: string | null
  patient_last_name?: string | null
  patient_dob?: string | null
  patient_gender?: PatientGender | null
  patient_phone?: string | null
  patient_middle_name?: string | null
  patient_address?: PatientAddress | null

  // Animal
  animal_name?: string | null
  animal_species?: string | null
  animal_breed?: string | null
  owner_name?: string | null
  owner_phone?: string | null
  patient_weight?: string | null
  patient_weight_unit?: string | null
  animal_dob_unknown?: boolean

  // Shared optional email: the patient's, or the owner's for an animal
  patient_email?: string | null

  // Shared allergies / medications
  patient_allergies?: string | null
  no_known_allergies?: boolean
  current_medications?: string | null
  no_current_medications?: boolean

  // Shared diagnosis / payment / delivery
  diagnosis_code?: string | null
  payment_type?: PaymentType | null
  delivery_method?: DeliveryMethod | null
}

/**
 * Per-product line item in a bulk submission. Only the product reference
 * and any overrides from catalog defaults.
 */
export interface PrescriptionLineItem {
  catalog_product_id: string // UUID
  dose_specific_directions?: string | null
  quantity?: string | null
  unit_amount?: string | null
  days_supply?: number | null
  refills?: number | null
  custom_field_values?: Record<string, string | number | boolean> | null
}

/**
 * Max line items the bulk endpoint accepts in ONE request. Mirrors
 * `PrescriptionBulkSubmit.prescriptions` max_length in
 * backend/app/schemas/portal.py. Items are processed sequentially on the
 * request path (PDF + S3 + fax each), so this bounds a single request's
 * duration against the 60s proxy timeout.
 */
export const BULK_MAX_LINE_ITEMS_PER_REQUEST = 10

/**
 * Max products the prescription form can hold per submission (PORTAL-190).
 * The form splits a submission into requests of at most
 * `BULK_MAX_LINE_ITEMS_PER_REQUEST`. Mirrors `_MAX_PRODUCTS` in
 * backend/app/schemas/prescription_draft.py.
 */
export const MAX_PRODUCTS_PER_SUBMISSION = 30

/**
 * Bulk submission payload — sent to `POST /api/portal/{slug}/prescriptions/bulk`.
 * Max `BULK_MAX_LINE_ITEMS_PER_REQUEST` prescriptions per request.
 */
export interface PrescriptionBulkSubmit {
  patient: PrescriptionPatientPayload
  practice_address_id?: string | null // UUID
  signature?: string | null // base64 PNG data URL
  prescriptions: PrescriptionLineItem[]
  /** Order-level general custom field values (PORTAL-164), shared by every line item. */
  general_custom_field_values?: Record<string, string | number | boolean> | null
}

export interface BulkPrescriptionResult {
  index: number
  catalog_product_id: string // UUID
  status: 'submitted' | 'failed'
  prescription_id: string | null
  reference_id: string | null
  error: string | null
}

export interface BulkPrescriptionResponse {
  results: BulkPrescriptionResult[]
}

/* --------------------------------------------------------------------------
   Prescription drafts ("save script for later" — PORTAL-61)
   -------------------------------------------------------------------------- */

/**
 * One saved product line. Unlike `PrescriptionLineItem` (which sends null for
 * any field equal to the catalog default), a draft stores the FULL on-screen
 * values for every editable field so the form restores exactly as saved.
 */
export interface DraftLineItem {
  catalog_product_id: string // UUID
  dose_specific_directions?: string | null
  quantity?: string | null
  unit_amount?: string | null
  days_supply?: number | null
  refills?: number | null
  custom_field_values?: Record<string, string | number | boolean> | null
  // Captured at save time so the queue list needs no catalog lookup.
  product_display_name?: string | null
}

/**
 * Permissive "save for later" payload. Everything is optional — a draft may be
 * partially filled — and the signature is included when the user signed before
 * choosing Save. Sent to `POST/PUT /api/portal/{slug}/prescriptions/drafts`.
 */
export interface PrescriptionDraftSave {
  patient?: PrescriptionPatientPayload | null
  practice_address_id?: string | null // UUID
  signature?: string | null // base64 PNG data URL
  prescriptions: DraftLineItem[]
  /** Order-level general custom field values (PORTAL-164). */
  general_custom_field_values?: Record<string, string | number | boolean> | null
}

export interface PrescriptionDraftSaveResponse {
  id: string // UUID
  updated_at: string // ISO datetime
}

export interface PrescriptionDraftListItem {
  id: string // UUID
  patient_name: string | null
  product_summary: string | null
  product_count: number
  has_signature: boolean
  created_at: string // ISO datetime
  updated_at: string // ISO datetime
}

export interface PrescriptionDraftListResponse {
  drafts: PrescriptionDraftListItem[]
  total: number
}

/** Full draft used to prefill the form. `payload` mirrors `PrescriptionDraftSave`. */
export interface PrescriptionDraftDetail {
  id: string // UUID
  payload: PrescriptionDraftSave
  created_at: string // ISO datetime
  updated_at: string // ISO datetime
}

export interface PrescriptionListItem {
  id: string // UUID
  reference_id: string
  status: PrescriptionStatus
  drug_name: string
  product_code: string
  patient_type: string
  patient_first_name: string | null
  patient_last_name: string | null
  patient_dob: string | null
  patient_gender: PatientGender | null
  animal_name: string | null
  animal_species: string | null
  owner_name: string | null
  has_pdf: boolean
  submitted_at: string // ISO datetime
  created_at: string // ISO datetime
  clinic_name: string | null
  // Null unless the pharmacy has shipment tracking enabled AND the package has
  // shipped (PORTAL-146). Consumed by partner integrations; not rendered in the
  // provider UI (the admin Faxes page shows it — PORTAL-186).
  shipment?: ShipmentInfo | null
  // Tracking number the pharmacy typed on its Faxes page, shown in the History
  // tab. Null unless the pharmacy has manual tracking enabled (PORTAL-192).
  manual_tracking_number?: string | null
}

export interface ShipmentInfo {
  carrier: string | null
  tracking_number: string | null
  shipping_service: string | null
  shipped_at: string | null // ISO datetime
}

export interface PdfDownloadResponse {
  url: string
}

export interface PrescriptionListResponse {
  prescriptions: PrescriptionListItem[]
  total: number
  limit: number
  offset: number
}

export interface PatientSummary {
  patient_type: string
  patient_first_name: string | null
  patient_last_name: string | null
  patient_middle_name: string | null
  patient_dob: string | null
  patient_gender: PatientGender | null
  patient_phone: string | null
  patient_email: string | null
  patient_address: PatientAddress | null
  patient_allergies: string | null
  no_known_allergies: boolean
  current_medications: string | null
  no_current_medications: boolean
  animal_name: string | null
  animal_species: string | null
  animal_breed: string | null
  owner_name: string | null
  owner_phone: string | null
  patient_weight: string | null
  patient_weight_unit: string | null
  animal_dob_unknown: boolean
  display_name: string
  last_prescribed_at: string // ISO datetime
}

export interface PatientListResponse {
  patients: PatientSummary[]
  total: number
}

export interface ProviderPrescriptionDetail {
  id: string // UUID
  reference_id: string
  catalog_product_id: string | null // UUID

  // Patient type
  patient_type: PatientType

  // Patient info (decrypted)
  patient_first_name: string | null
  patient_last_name: string | null
  patient_middle_name: string | null
  patient_dob: string | null
  patient_gender: PatientGender | null
  patient_phone: string | null
  patient_email: string | null
  patient_address: PatientAddress | null
  patient_allergies: string | null
  no_known_allergies: boolean
  current_medications: string | null
  no_current_medications: boolean

  // Animal patient info
  animal_name: string | null
  animal_species: string | null
  animal_breed: string | null
  owner_name: string | null
  owner_phone: string | null
  patient_weight: string | null
  patient_weight_unit: string | null
  animal_dob_unknown: boolean

  // Drug information
  drug_name: string
  product_code: string
  drug_ndc: string | null
  ingredients: string | null

  // Prescription details
  strength: string | null
  concentration: string | null // historical
  dose_amount: string | null // historical
  dose_specific_directions: string
  default_additional_directions: string | null
  quantity_per_product: string | null // historical
  quantity: string // Decimal as string
  unit_amount: string | null
  quantity_unit: string // historical
  days_supply: number
  refills: number

  // Custom field values
  custom_field_values: Record<string, string | number | boolean> | null
  /** General (pharmacy-level) custom field values (PORTAL-164). */
  general_custom_field_values?: Record<string, string | number | boolean> | null

  // Diagnosis, payment, delivery
  diagnosis_code: string | null
  payment_type: PaymentType | null
  delivery_method: DeliveryMethod | null

  // Status
  status: PrescriptionStatus
  submitted_at: string // ISO datetime
}

/* --------------------------------------------------------------------------
   Provider Types (matching backend/app/schemas/provider.py)
   -------------------------------------------------------------------------- */

export interface ProviderInvite {
  email: string
  name: string
  npi?: string | null
  password?: string | null
}

export interface ProviderUpdate {
  name?: string | null
  npi?: string | null
  license_number?: string | null
  license_state?: string | null
  phone?: string | null
  fax?: string | null
  status?: ProviderStatus | null
}

export interface ProviderResponse {
  id: string // UUID
  pharmacy_id: string // UUID
  email: string
  name: string | null
  npi: string | null
  license_number: string | null
  license_state: string | null
  phone: string | null
  fax: string | null
  status: ProviderStatus
  profile_complete: boolean
  has_custom_catalog: boolean
  pricing_list_id: string | null // UUID of the assigned pricing list; null = default prices (PORTAL-204)
  primary_clinic_name: string | null // office_name of the primary practice address
  address_count: number // total practice addresses on file
  // Effective (per-provider resolved) country-field setting (PORTAL-72) — lets
  // the addresses modal skip a separate settings fetch. Null on responses that
  // don't resolve it, so callers fall back to the pharmacy global.
  effective_allow_country_field: boolean | null
  last_login_at: string | null // ISO datetime string
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
}

// A single provider's full details for the pharmacy team (PORTAL-74). Only
// the single-provider GET returns these — the list stays thin (no DEA).
export interface ProviderDetailResponse extends ProviderResponse {
  dea_number: string | null
  credential_type: CredentialType | null
  supervising_physician: string | null
  supervising_physician_npi: string | null
  is_veterinarian: boolean
  // True until the provider replaces a pharmacy-set password (PORTAL-75)
  must_change_password: boolean
  invitation_expires: string | null // ISO datetime string
  /** Authenticator-app 2FA turned on (PORTAL-187). */
  two_factor_enabled: boolean
}

export interface ProviderListResponse {
  providers: ProviderResponse[]
  total: number
}

export interface CatalogAssignmentUpdate {
  product_ids: string[] // UUIDs
}

export interface CatalogAssignmentResponse {
  provider_id: string // UUID
  product_ids: string[] // UUIDs
  has_custom_catalog: boolean
}

/* --------------------------------------------------------------------------
   Per-Provider Setting Overrides (PORTAL-72)
   -------------------------------------------------------------------------- */

// Update payload: each field is a tri-state override of the pharmacy default.
// null = clear the override (inherit); true/false = explicit override; an
// omitted field is left unchanged.
export interface ProviderSettingsUpdate {
  signature_required_override?: boolean | null
  npi_required_override?: boolean | null
  dea_required_override?: boolean | null
  phone_required_override?: boolean | null
  provider_address_lock_override?: boolean | null
  allow_country_field_override?: boolean | null
  patient_address_required_override?: boolean | null
  hide_payment_delivery_section_override?: boolean | null
  hide_diagnosis_code_override?: boolean | null
  hide_payment_type_override?: boolean | null
  hide_delivery_method_override?: boolean | null
  diagnosis_code_required_override?: boolean | null
  payment_type_required_override?: boolean | null
  delivery_method_required_override?: boolean | null
  // Per-provider veterinarian designation (PORTAL-78). Plain boolean, not an
  // override — only settable when NPI is not required for the provider.
  is_veterinarian?: boolean | null
}

// A full set of resolved boolean settings (pharmacy defaults or effective).
export interface ProviderSettingsValues {
  signature_required: boolean
  npi_required: boolean
  dea_required: boolean
  phone_required: boolean
  provider_address_lock: boolean
  allow_country_field: boolean
  patient_address_required: boolean
  hide_payment_delivery_section: boolean
  hide_diagnosis_code: boolean
  hide_payment_type: boolean
  hide_delivery_method: boolean
  diagnosis_code_required: boolean
  payment_type_required: boolean
  delivery_method_required: boolean
}

export interface ProviderSettingsResponse {
  provider_id: string // UUID
  signature_required_override: boolean | null
  npi_required_override: boolean | null
  dea_required_override: boolean | null
  phone_required_override: boolean | null
  provider_address_lock_override: boolean | null
  allow_country_field_override: boolean | null
  patient_address_required_override: boolean | null
  hide_payment_delivery_section_override: boolean | null
  hide_diagnosis_code_override: boolean | null
  hide_payment_type_override: boolean | null
  hide_delivery_method_override: boolean | null
  diagnosis_code_required_override: boolean | null
  payment_type_required_override: boolean | null
  delivery_method_required_override: boolean | null
  // Per-provider veterinarian designation (PORTAL-78). Plain boolean, not an
  // override; surfaced here so the Override Settings page needs no extra fetch.
  is_veterinarian: boolean
  // The pharmacy global defaults (inherited when not overridden) and the
  // resolved effective values for this provider.
  defaults: ProviderSettingsValues
  effective: ProviderSettingsValues
}

/* --------------------------------------------------------------------------
   Profile Types (matching backend/app/schemas/profile.py)
   -------------------------------------------------------------------------- */

export interface ProfileUpdate {
  name?: string | null
  npi?: string | null
  license_number?: string | null
  license_state?: string | null
  phone?: string | null
  fax?: string | null
  address_street?: string | null
  address_city?: string | null
  address_state?: string | null
  address_zip?: string | null
  dea_number?: string | null
  credential_type?: CredentialType | null
  supervising_physician?: string | null
  supervising_physician_npi?: string | null
}

export interface ProfileResponse {
  id: string // UUID
  pharmacy_id: string // UUID
  email: string
  name: string | null
  npi: string | null
  license_number: string | null
  license_state: string | null
  phone: string | null
  fax: string | null
  // Legacy mirror of the primary practice address (kept for backward
  // compatibility). Prefer `addresses` for multi-address UI.
  address_street: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  dea_number: string | null
  credential_type: CredentialType | null
  supervising_physician: string | null
  supervising_physician_npi: string | null
  status: ProviderStatus
  profile_complete: boolean
  // When true, the provider was given a pharmacy-set password and must change
  // it before using the portal (PORTAL-75).
  must_change_password: boolean
  // Authenticator-app 2FA turned on (PORTAL-187).
  two_factor_enabled: boolean
  // Multi-address practice list — primary first (issues #140 / #180).
  // Each address carries its own locked payment/delivery defaults (#192).
  addresses: ProviderAddressResponse[]
  // When true, the pharmacy manages addresses on the provider's behalf
  address_lock: boolean
  // When true, the practice-address editor should expose the country field (#190)
  allow_country_field: boolean
  // Effective (provider-resolved) portal settings (PORTAL-72). These apply the
  // per-provider override over the pharmacy global; the provider UI reads these
  // so overrides take effect. `address_lock`/`allow_country_field` above are
  // also effective.
  signature_required: boolean
  npi_required: boolean
  dea_required: boolean
  phone_required: boolean
  patient_address_required: boolean
  hide_payment_delivery_section: boolean
  hide_diagnosis_code: boolean
  hide_payment_type: boolean
  hide_delivery_method: boolean
  diagnosis_code_required: boolean
  payment_type_required: boolean
  delivery_method_required: boolean
  // Pharmacy-wide note published by this provider's pharmacy (PORTAL-159).
  // A Markdown subset, not HTML. Null when the pharmacy has published none,
  // which is what hides the "Pharmacy Notes" tab.
  pharmacy_notes: string | null
  // General (pharmacy-level) custom fields (PORTAL-164), rendered at the end of
  // the New Prescription form. Only served to authenticated providers.
  general_custom_fields?: CustomFieldDefinition[] | null
  last_login_at: string | null // ISO datetime string
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
}

/* --------------------------------------------------------------------------
   Provider Practice Addresses (issues #140 / #180)
   -------------------------------------------------------------------------- */

export interface ProviderAddressResponse {
  id: string // UUID
  provider_id: string // UUID
  office_name: string | null
  street: string
  city: string
  state: string
  zip: string
  // ISO-3166 alpha-2 country code (issue #190). Defaults to 'US'.
  country: string
  is_primary: boolean
  // Pharmacy-configured options scoped to this address (#192, PORTAL-84).
  // Empty: provider chooses. One value: the prescription form locks the field
  // to it. Several: the form only offers those options.
  default_payment_types: PaymentType[]
  default_delivery_methods: DeliveryMethod[]
  created_at: string
  updated_at: string
}

export interface ProviderAddressCreate {
  office_name?: string | null
  street: string
  city: string
  state: string
  zip: string
  country?: string
  is_primary?: boolean
  default_payment_types?: PaymentType[]
  default_delivery_methods?: DeliveryMethod[]
}

export interface ProviderAddressUpdate {
  office_name?: string | null
  street?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  is_primary?: boolean
  default_payment_types?: PaymentType[]
  default_delivery_methods?: DeliveryMethod[]
}

export interface ProviderAddressListResponse {
  addresses: ProviderAddressResponse[]
  total: number
  can_manage: boolean
}

/* --------------------------------------------------------------------------
   API Error Types
   -------------------------------------------------------------------------- */

export interface ApiErrorDetail {
  loc?: (string | number)[]
  msg: string
  type?: string
}

export interface ApiError {
  detail: string | ApiErrorDetail[]
}

export interface ValidationError {
  detail: ApiErrorDetail[]
}

/* --------------------------------------------------------------------------
   Admin Types (matching backend/app/schemas/pharmacy_user.py)
   -------------------------------------------------------------------------- */

export const PharmacyUserRole = {
  ADMIN: 'admin', // account owner — full access + manages everyone
  MANAGER: 'manager', // all tasks except managing other managers/admins
  STAFF: 'staff', // provider-related tasks only
} as const

export type PharmacyUserRole =
  (typeof PharmacyUserRole)[keyof typeof PharmacyUserRole]

export const PHARMACY_USER_ROLE_LABELS: Record<PharmacyUserRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
}

export const PharmacyUserStatus = {
  INVITED: 'invited',
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const

export type PharmacyUserStatus =
  (typeof PharmacyUserStatus)[keyof typeof PharmacyUserStatus]

export interface PharmacyUserResponse {
  id: string // UUID
  pharmacy_id: string // UUID
  email: string
  name: string
  role: PharmacyUserRole
  status: PharmacyUserStatus
  last_login_at: string | null // ISO datetime string
  /** Authenticator-app 2FA turned on (PORTAL-187). Absent on JWT-built users. */
  two_factor_enabled?: boolean
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
}

export interface PharmacyUserListResponse {
  users: PharmacyUserResponse[]
  total: number
  /** Per-role max non-disabled users, from the backend authz caps (PORTAL-94). */
  limits: Record<PharmacyUserRole, number>
}

export interface PharmacyUserInvite {
  email: string
  name: string
  role: PharmacyUserRole
}

export interface PharmacyUserUpdate {
  name?: string
  role?: PharmacyUserRole
  status?: PharmacyUserStatus
}

/* --------------------------------------------------------------------------
   Pharmacy Types (matching backend/app/schemas/pharmacy.py)
   -------------------------------------------------------------------------- */

export interface AddressSchema {
  street: string
  city: string
  state: string // US: 2-char state code; CA: province code
  zip: string // US: 5 or 5+4 format; CA: A1A 1A1
  country?: string // ISO-3166 alpha-2; defaults to 'US' server-side (PORTAL-161)
}

export interface PharmacyResponse {
  id: string // UUID
  name: string
  phone: string
  fax_number: string
  address: AddressSchema | null
  npi: string | null
  state_license: string | null
  portal_slug: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  is_active: boolean
  landing_page_enabled: boolean
  show_catalog_on_landing_page: boolean
  show_invite_form_on_landing_page: boolean
  portal_type: PortalType
  /** ISO-3166 alpha-2 (PORTAL-161): 'US' | 'CA'. */
  country: string
  signature_required: boolean
  npi_required: boolean
  dea_required: boolean
  phone_required: boolean
  patient_address_required: boolean
  provider_address_lock: boolean
  allow_country_field: boolean
  hide_payment_delivery_section: boolean
  hide_diagnosis_code: boolean
  hide_payment_type: boolean
  hide_delivery_method: boolean
  diagnosis_code_required: boolean
  payment_type_required: boolean
  delivery_method_required: boolean
  /** PORTAL-192: tracking-number inputs on the Faxes page (set via sqladmin). */
  manual_tracking_enabled: boolean
  hide_machine_readable_zone: boolean
  /** Pharmacy-wide note shown to every provider (Markdown subset, not HTML) */
  pharmacy_notes: string | null
  /** General (pharmacy-level) custom fields (PORTAL-164). */
  general_custom_fields?: CustomFieldDefinition[] | null
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
}

export interface PharmacyUpdate {
  name?: string | null
  phone?: string | null
  address?: AddressSchema | null
  address_verified?: boolean
  country?: string
  npi?: string | null
  state_license?: string | null
  logo_url?: string | null
  primary_color?: string | null
  secondary_color?: string | null
  is_active?: boolean | null
  landing_page_enabled?: boolean | null
  show_catalog_on_landing_page?: boolean | null
  show_invite_form_on_landing_page?: boolean | null
  portal_type?: PortalType | null
  signature_required?: boolean | null
  npi_required?: boolean | null
  dea_required?: boolean | null
  phone_required?: boolean | null
  patient_address_required?: boolean | null
  provider_address_lock?: boolean | null
  allow_country_field?: boolean | null
  hide_payment_delivery_section?: boolean | null
  hide_diagnosis_code?: boolean | null
  hide_payment_type?: boolean | null
  hide_delivery_method?: boolean | null
  diagnosis_code_required?: boolean | null
  payment_type_required?: boolean | null
  delivery_method_required?: boolean | null
  /** Not nullable: the backend rejects null with a 422. */
  hide_machine_readable_zone?: boolean
  /** Send "" to clear the note; the backend stores a blank note as null. */
  pharmacy_notes?: string | null
  /** General custom fields (PORTAL-164). Send [] to clear. */
  general_custom_fields?: CustomFieldDefinition[] | null
}

/* --------------------------------------------------------------------------
   Admin Catalog Types (matching backend/app/schemas/catalog_product.py)
   Full admin version with is_active and all fields
   -------------------------------------------------------------------------- */

/* Category management (PORTAL-77) */

export interface CategoryBrief {
  id: string // UUID
  name: string
}

export interface Category extends CategoryBrief {
  product_count: number
}

export interface CategoryListResponse {
  categories: Category[]
  total: number
}

/* Pricing lists (PORTAL-204). Display-only: providers never pay in the portal. */

export interface PricingList {
  id: string // UUID
  name: string
  description: string | null
  provider_count: number
  entry_count: number
  fixed_count: number
  percent_count: number
  free_count: number
  percent_min: string | null // Decimal string, e.g. "-15.00"
  percent_max: string | null
  updated_at: string // ISO datetime string
}

export interface PricingListListResponse {
  lists: PricingList[]
  total: number
  product_total: number
  default_priced_count: number
  default_provider_count: number
}

export type PricingListStartFrom = 'blank' | 'duplicate' | 'percent_off'

export interface PricingListCreate {
  name: string
  description?: string | null
  start_from: PricingListStartFrom
  source_list_id?: string | null
  percent_off?: string | null
}

export interface PricingListUpdate {
  name?: string
  description?: string | null
}

export interface PricingListAssignmentResponse {
  pricing_list_id: string | null
  updated: number
}

export interface CategoryCreate {
  name: string
}

export interface CatalogProductCreate {
  display_name: string
  product_code: string
  ndc?: string | null
  ingredients?: string | null
  strength?: string | null
  dose_specific_directions?: string | null
  default_additional_directions?: string | null
  quantity?: string | null // Decimal as string
  unit_amount: string
  days_supply?: number | null
  refills?: number | null // 0-11
  category_ids?: string[] | null
  custom_fields?: CustomFieldDefinition[] | null
  product_notes?: ProductNoteDefinition[] | null
  medical_necessity_statement?: string | null
}

export interface CatalogProductUpdate {
  display_name?: string | null
  product_code?: string | null
  ndc?: string | null
  ingredients?: string | null
  strength?: string | null
  dose_specific_directions?: string | null
  default_additional_directions?: string | null
  quantity?: string | null // Decimal as string
  unit_amount?: string | null
  days_supply?: number | null
  refills?: number | null // 0-11
  category_ids?: string[] | null
  is_active?: boolean | null
  custom_fields?: CustomFieldDefinition[] | null
  product_notes?: ProductNoteDefinition[] | null
  medical_necessity_statement?: string | null
}

export interface CatalogProductResponse {
  id: string // UUID
  pharmacy_id: string // UUID
  display_name: string
  product_code: string
  ndc: string | null
  ingredients: string | null
  strength: string | null
  dose_specific_directions: string | null
  default_additional_directions: string | null
  quantity: string | null // Decimal as string
  unit_amount: string
  days_supply: number | null
  refills: number | null
  categories: CategoryBrief[]
  custom_fields: CustomFieldDefinition[] | null
  product_notes: ProductNoteDefinition[] | null
  medical_necessity_statement: string | null
  is_active: boolean
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
}

export interface CatalogProductListResponse {
  products: CatalogProductResponse[]
  total: number
}

/* --------------------------------------------------------------------------
   Access Request Types (matching backend/app/schemas/access_request.py)
   -------------------------------------------------------------------------- */

export const AccessRequestStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
} as const

export type AccessRequestStatus =
  (typeof AccessRequestStatus)[keyof typeof AccessRequestStatus]

export interface AccessRequestCreate {
  name: string
  email: string
  npi?: string | null
  license_state?: string | null
  phone?: string | null
  message?: string | null
}

export interface AccessRequestResponse {
  id: string // UUID
  pharmacy_id: string // UUID
  name: string
  email: string
  npi: string | null
  license_state: string | null
  phone: string | null
  message: string | null
  status: AccessRequestStatus
  deny_reason: string | null
  reviewed_by: string | null // UUID
  reviewed_at: string | null // ISO datetime
  created_at: string // ISO datetime
  updated_at: string // ISO datetime
}

export interface AccessRequestListResponse {
  requests: AccessRequestResponse[]
  total: number
  pending_count: number
}

export interface AccessRequestDeny {
  reason?: string | null
}

/* --------------------------------------------------------------------------
   Public Catalog Types (for landing page)
   -------------------------------------------------------------------------- */

export interface PublicCatalogProductDetail {
  display_name: string
  product_code: string
  ndc: string | null
  categories: string[]
  strength: string | null
  dose_specific_directions: string | null
  default_additional_directions: string | null
  quantity: string | null // Decimal as string
  unit_amount: string
  days_supply: number | null
  refills: number | null
}

export interface PublicCatalogResponse {
  products: PublicCatalogProductDetail[]
  total: number
}

/* --------------------------------------------------------------------------
   CSV Catalog Import
   -------------------------------------------------------------------------- */

export interface CsvImportError {
  row: number
  field: string
  message: string
  /** Spreadsheet cell of the offending value (header = row 1), e.g. "F8" */
  cell: string | null
  product_code: string | null
  display_name: string | null
  /** The rejected input value */
  value: string | null
}

export interface CsvFieldChange {
  field: string
  old: string
  new: string
}

export interface CsvProductChange {
  product_code: string
  display_name: string
  changes: CsvFieldChange[]
}

export interface CsvImportResult {
  inserted: number
  updated: number
  unchanged: number
  errors: CsvImportError[]
  dry_run: boolean
  to_create: CsvProductChange[]
  to_update: CsvProductChange[]
  new_categories: string[]
}
